import { clearFocus, setFocus } from '@bpd-library/components';
import { ExtendedConstructionNumber, ExtendedPropertyType } from '@bpd-library/utilities';
import { HouseSelectorElement } from '@features/properties/components/house-selector';
import { getCurrentView, getHouseSelectorViewByObjectId } from '@features/properties/store';
import { virtualView2dPointerEvents } from '@features/virtual-tour/constants';

import { HouseSelectorView } from '../../house-selector/utilities/types';

const useContentInteraction = (options: {
    houseSelectorElement?: { current: HouseSelectorElement | null };
}) => {
    function onMouseenter(_: MouseEvent, item: ExtendedConstructionNumber | ExtendedPropertyType) {
        const { type } = getCurrentView() || {};

        switch (type) {
            case 'constructionNumber':
                setFocus([item.id], { static: true });
                houseSelectorPolygonFocus([item.id], true);
                break;
            case 'apartmentConstructionNumber':
                houseSelectorPolygonFocus([item.id], true);
                virtualTourLabelFocus(item as ExtendedConstructionNumber, true);
                break;
            case 'propertyType':
                item = item as ExtendedPropertyType;
                const constructionNumberIds = item.constructionNumbers?.map(({ id }) => id) || [];
                const apartmentBuildingIds = item.apartmentBuildings?.map(({ id }) => id) || [];
                const focusIds = [item.id, ...constructionNumberIds, ...apartmentBuildingIds];

                /*
                 * In some cases the propertyType polygon is drawn instead of constructionNumbers.
                 * Add both constructionNumber and item id to be sure.
                 */

                houseSelectorPolygonFocus(focusIds, true);
                setFocus(focusIds);

                break;
        }
    }

    function onMouseleave(_: MouseEvent, item: ExtendedConstructionNumber | ExtendedPropertyType) {
        const { type } = getCurrentView() || {};

        houseSelectorPolygonFocus([item.id], false);

        if (type !== 'apartmentConstructionNumber') clearFocus();
        else virtualTourLabelFocus(item as ExtendedConstructionNumber, false);
    }

    function virtualTourLabelFocus(item: ExtendedConstructionNumber, active: boolean) {
        const event = new CustomEvent(virtualView2dPointerEvents.LABEL_FOCUS, {
            detail: {
                active,
                id: item.id,
            },
        });

        return document.dispatchEvent(event);
    }

    async function houseSelectorPolygonFocus(apiObjectIds: string[], active: boolean) {
        const houseSelector = options?.houseSelectorElement?.current;
        if (!houseSelector) return;

        if (active) {
            houseSelector.hoverDisabled = true;
            let focusPolygons = houseSelector.currentView?.polygons?.filter(
                ({ objectId }) => objectId && apiObjectIds.includes(objectId),
            );

            if (!focusPolygons?.length) {
                let polygonView: HouseSelectorView | undefined;

                apiObjectIds.find((id) => {
                    polygonView = getHouseSelectorViewByObjectId(id);
                    return !!polygonView;
                });

                polygonView && (await houseSelector.setView(polygonView));

                focusPolygons = houseSelector.currentView?.polygons?.filter(
                    ({ objectId }) => objectId && apiObjectIds.includes(objectId),
                );
                if (!focusPolygons?.length) return;
            }

            houseSelector.highlightedPolygons = focusPolygons ?? null;
        } else {
            houseSelector.hoverDisabled = false;
            houseSelector.highlightedPolygons = null;
        }
    }

    return [onMouseenter, onMouseleave];
};

export { useContentInteraction };
